import { useCallback, useEffect, useRef, useState } from 'react';

import { CustomStackPicker, fetchStackCatalogue, StackPreset } from '../services/projectStacks';

/** The tech stacks the server offers a new Web project, as last read: its presets
 *  and the Custom option's picker, null while that isn't offered. */
export type ProjectStacksState =
  | { kind: 'idle' | 'loading' | 'error' }
  | { kind: 'loaded'; presets: StackPreset[]; custom: CustomStackPicker | null };

/**
 * The tech stacks GET /projects/stacks returns, read each time `active` turns
 * true and on `reload`. So a stack the server stops offering (the switch turned
 * off, or a part.json or picker.json no longer offering it) leaves the list the
 * next time a drawer opens, without a page reload.
 *
 * A list already read stays on screen while it is read again, and when that
 * read fails: the server checks the choice anyway.
 */
export function useProjectStacks(active: boolean) {
  const [state, setState] = useState<ProjectStacksState>({ kind: 'idle' });
  // Drops replies that a later read overtook.
  const readSeq = useRef(0);

  const reload = useCallback(async () => {
    const id = ++readSeq.current;
    setState((current) => (current.kind === 'loaded' ? current : { kind: 'loading' }));
    try {
      const { presets, custom } = await fetchStackCatalogue();
      if (id === readSeq.current) setState({ kind: 'loaded', presets, custom });
    } catch {
      if (id === readSeq.current) setState((current) => (current.kind === 'loaded' ? current : { kind: 'error' }));
    }
  }, []);

  useEffect(() => {
    if (active) reload();
  }, [active, reload]);

  return { state, reload };
}
